import React, { useState } from "react";
import {
  PortfolioOverviewContainer,
  TopSectionContainer,
  TopSectionRightContainer,
  TopSectionLeftContainer,
  PerformanceDataContainer,
  GrowthContainer,
  TitleWithIcon,
  TooltipWrapper,
} from "./styled";
import { ThemedText } from "@components/ThemedText";
import { useTheme } from "styled-components";
import ArrowUpIcon from "@icon-park/react/es/icons/ArrowUp";
import ArrowDownIcon from "@icon-park/react/es/icons/ArrowDown";
import InfoIcon from "@icon-park/react/es/icons/Info";
import useCurrency from "@helpers/useCurrency";
import { Currency, ExchangeRates } from "../types";
import GlossaryModal from "@components/Glossary/Glossary.component";

interface PortfolioOverviewProps {
  value: number;
  growth: number;
  earnings: number;
  reservedForInvestment: number;
  currency: Currency;
  exchangeRates: ExchangeRates;
  showExchangeRate?: boolean;
}

const PortfolioOverview = ({
  value = 0,
  growth = 0,
  earnings = 0,
  currency = "GBP",
  exchangeRates,
  showExchangeRate = false,
}: PortfolioOverviewProps) => {
  const theme = useTheme();
  const [showGlossary, setShowGlossary] = useState(false);
  const { toCurrency } = useCurrency(currency);

  return (
    <PortfolioOverviewContainer>
      <TopSectionContainer>
        <TopSectionRightContainer>
          <TitleWithIcon>
            <ThemedText type="H18" color="SLATE800">
              Current Portfolio Value
            </ThemedText>
            <TooltipWrapper onClick={() => setShowGlossary(true)}>
              <InfoIcon size={16} fill={theme.colors.ui.icon.secondary} />
            </TooltipWrapper>
          </TitleWithIcon>
          <ThemedText type="H32" color="SLATE900">
            {toCurrency(value)}
          </ThemedText>
        </TopSectionRightContainer>
        <TopSectionLeftContainer>
          <PerformanceData
            currency={currency}
            growth={growth}
            earnings={earnings}
          />
          {showExchangeRate && (
            <ThemedText type="P12" color="SLATE600">
              Exchange Rate: 1 GBP ≈ {exchangeRates.GBP_TO_USD} USD
            </ThemedText>
          )}
        </TopSectionLeftContainer>
      </TopSectionContainer>

      <GlossaryModal
        show={showGlossary}
        onClose={() => setShowGlossary(false)}
        glossaryType="PORTFOLIO"
      />
    </PortfolioOverviewContainer>
  );
};

export default PortfolioOverview;

interface PerformanceDataProps {
  currency: Currency;
  growth: number;
  earnings: number;
  variant?: "small" | "large";
}

export const PerformanceData = ({
  currency,
  growth,
  earnings,
  variant = "large",
}: PerformanceDataProps) => {
  const theme = useTheme();
  const Icon = growth > 0 ? ArrowUpIcon : ArrowDownIcon;
  const iconColor = growth > 0 ? "GREEN800" : "ERROR800";
  const iconFill =
    growth > 0 ? theme.colors.ui.text.success : theme.colors.ui.text.error;
  const { toCurrency } = useCurrency(currency);
  const textSize = variant === "small" ? "S14" : "H18";
  return (
    <PerformanceDataContainer>
      {!!earnings && (
        <GrowthContainer>
          <ThemedText forwardedAs="span" type={textSize} color={iconColor}>
            {growth > 0 ? "+" : ""}
            {toCurrency(earnings)}
          </ThemedText>
        </GrowthContainer>
      )}
      <GrowthContainer>
        {!!growth && (
          <>
            <Icon fill={iconFill} />
            <ThemedText forwardedAs="span" type={textSize} color={iconColor}>
              {growth}%
            </ThemedText>
          </>
        )}
      </GrowthContainer>
    </PerformanceDataContainer>
  );
};
